/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { SnippetsSectionProps } from '../Snippets/types';
import { type ReactNode, useContext } from 'react';
import { Code } from './Code';
import { CustomerPortalContext } from '../../../contexts/CustomerPortalContext';
import { EvaluationLicenseContext } from '../../../contexts/EvaluationLicenseContext';
import { getCodeJs } from '../../../utils/getCodeJs/getCodeJs';
import { getFilename } from '../../../utils/getFilename/getFilename';
import { HighlightCode } from '../../HighlightCode/HighlightCode';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Text } from './Text';
import { type Technology } from '../../../constants/installation';
import {
	SECTION_NAME_JS,
	SECTION_NAME_REACT_COMPONENT,
	SECTION_NAME_VUE_COMPONENT,
	SECTION_NAME_ANGULAR_COMPONENT
} from '../../../constants/snippetsSectionNames';
import { getCodeReactJs } from '../../../utils/getCodeReactJs/getCodeReactJs';
import { getCodeVueJs } from '../../../utils/getCodeVueJs/getCodeVueJs';
import { getCodeAngular } from '../../../utils/getCodeAngular/getCodeAngular';
import { OutputBanner } from '../OutputBanner/OutputBanner';
import { EditorSettingsContext } from '../../../contexts/EditorSettingsContext';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

export function Js( {
	isLoading,
	number,
	onCopyEvent,
	allFeaturesAvailableInPlan,
	arePremiumFeaturesSelected,
	hasPremiumPlugins
}: SnippetsSectionProps ): ReactNode {
	const { selectedTechnology, selectedInstallMethod, selectedCodeOutput } = useContext( InstallationSettingsContext );
	const { editorPlugins, editorType, editorLanguage, toolbarOptions, builderUrl } = useContext( EditorSettingsContext );
	const { isLoggedIn, customerPortalAccount } = useContext( CustomerPortalContext );
	const evaluationLicense = useContext( EvaluationLicenseContext );
	const editorOptions = { editorPlugins, editorType, editorLanguage, toolbarOptions };
	const sectionTitle = getSectionTitle( selectedTechnology )!;
	const highlightLang = getHighlightLang( selectedTechnology )!;
	const jsCodeFn = getJsCodeFn( selectedTechnology )!;

	const htmlFilename = getFilename( { type: 'html', selectedTechnology } );
	const cssFilename = getFilename( { type: 'css', selectedTechnology } );
	const jsFilename = getFilename( { type: 'js', selectedTechnology } );
	const vueMainJsFilename = getFilename( { type: 'vueMainJs', selectedTechnology } );

	const sameDirFiles: Array<string> = [];

	if ( selectedTechnology === 'js' && selectedInstallMethod === 'cdn' ) {
		sameDirFiles.push( htmlFilename );
	} else if ( selectedTechnology === 'js' || selectedTechnology === 'react' ) {
		sameDirFiles.push( cssFilename );
	} else if ( selectedTechnology === 'vue' ) {
		sameDirFiles.push( vueMainJsFilename, cssFilename );
	}

	const sameDirFilesList = sameDirFiles
		.map<React.ReactNode>( filename => <Code key={ filename }>{ filename }</Code> )
		.reduce( ( prev, curr ) => [ prev, ' and ', curr ], [] );

	return (
		<SkeletonLoader number={ number } sectionTitle={ sectionTitle } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ sectionTitle }>
				<Text>
					Create or edit an <Code>{ jsFilename }</Code> file in your project and paste the code from below.
					{
						sameDirFiles.length > 0 &&
							<span> Make sure it is created in the same directory as { sameDirFilesList }.</span>
					}
				</Text>
				<OutputBanner
					arePremiumFeaturesSelected={ arePremiumFeaturesSelected }
					allFeaturesAvailableInPlan={ allFeaturesAvailableInPlan }
					installMethod={ selectedInstallMethod }
					codeOutput={ selectedCodeOutput }
				/>
				<HighlightCode onCopyEvent={ onCopyEvent } lang={ highlightLang } id={ 'snippet-js' }>
					{ jsCodeFn( {
						customerPortalAccount,
						editorOptions,
						evaluationLicense,
						selectedInstallMethod,
						selectedTechnology,
						isLoggedIn,
						hasPremiumPlugins,
						builderUrl
					} ) }
				</HighlightCode>
			</SnippetsSection>
		</SkeletonLoader>
	);
}

function getSectionTitle( selectedTechnology: Technology ) {
	switch ( selectedTechnology ) {
		case 'js':
			return SECTION_NAME_JS;
		case 'react':
			return SECTION_NAME_REACT_COMPONENT;
		case 'vue':
			return SECTION_NAME_VUE_COMPONENT;
		case 'angular':
			return SECTION_NAME_ANGULAR_COMPONENT;
	}
}

function getHighlightLang( selectedTechnology: Technology ) {
	switch ( selectedTechnology ) {
		case 'js':
			return 'js';
		case 'react':
			return 'jsx';
		case 'vue':
			return 'vue';
		case 'angular':
			return 'angular-ts';
	}
}

function getJsCodeFn( selectedTechnology: Technology ) {
	switch ( selectedTechnology ) {
		case 'js':
			return getCodeJs;
		case 'react':
			return getCodeReactJs;
		case 'vue':
			return getCodeVueJs;
		case 'angular':
			return getCodeAngular;
	}
}
